// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2023-03-13 22:56:40 +0100 (Mon, 13 Mar 2023) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 132899 $
//  Purpose   : experimental openai interface components
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Loader, Message, Button} from 'semantic-ui-react'

// ---Framework imports
import {Sel, Actions} from 'uix/registries'
import log from 'uix/logging'
import {strip, isNil} from 'ldash'

import {
  useApi,
  useDebounce,
  useSelector,
  useState,
  useInView,
  useResettableState,
  useCallApi,
  usePhysicalNode,
} from 'uix/hooks'
import {JSONTree} from 'uix/chunks'
import {EditableLabel} from 'uix/components/editableLabel'

// ---Local imports

// ---Exports

const SeedButtons = ({
  retrySeed,
  setRetrySeed,
  disabled,
  style,
  floated,
  defaultSeed,
  children,
}) => {
  let seed = isNil(retrySeed) ? defaultSeed || 0 : retrySeed
  return (
    <Button.Group basic size="mini" floated={floated} style={{...style}}>
      <Button
        basic
        disabled={!seed || disabled}
        onClick={() => Math.max(0, setRetrySeed(seed - 1))}
        icon="arrow left"
      />
      <Button basic content={seed} />
      <Button basic disabled={disabled} onClick={() => setRetrySeed(seed + 1)} icon="arrow right" />
      {children}
    </Button.Group>
  )
}

const TocExperiment = ({prompt, text}) => {
  // XXX: we need a useWikiContext() here
  let node = usePhysicalNode()
  let {callApi} = useCallApi('openai/toc_experiment')
  if (node.datatype != 21 || node.subtype != 1) return null
  return (
    <Button basic icon="birthday cake" onClick={() => callApi({otid: node.otid, prompt, text})} />
  )
}

const MODELS = ['text-davinci-003', 'text-curie-001', 'text-babbage-001', 'text-ada-001']
export const AiPrompt = ({
  text,
  context,
  suffix,
  debug,
  model = 'text-davinci-003',
  interactive = false,
  seed = 0,
  header,
}) => {
  let [ref, inView] = useInView({triggerOnce: true, rootMargin: '100%'})

  let [interactiveText, setTnteractiveText] = useState(null)

  let [retrySeed, setRetrySeed] = useResettableState(null, [seed])
  if (retrySeed) {
    seed = retrySeed
  }

  let [debouncedText] = useDebounce(interactiveText || text, interactive ? 200 : 2000, {
    leading: true,
  })
  let strippedPrompt = strip(debouncedText)

  let active = strippedPrompt.split(' ').length > 1
  let inActiveReason = 'No Prompt'

  if (!MODELS.includes(model)) {
    active = false
    inActiveReason = 'Bad Model'
  }

  let contextualize = p => {
    if (context) {
      return `${context}\n${p}`
    }
    return p
  }

  let suffixualize = p => {
    if (suffix) {
      return `${p}\n${suffix}`
    }
    return p
  }

  let params = {prompt: suffixualize(contextualize(strippedPrompt)), model, seed}
  let {data, error, loading} = useApi('openai/completions', params, {
    method: 'post',
    condition: active && inView,
    timeout: Infinity,
    //~ autoreset: false,
  })
  let textOutput = data?.choices[0].text || ''

  let loader = null
  if (loading) {
    loader = <Loader active inline size="mini" />
  }
  let content = null
  if (!active && !interactive) {
    content = <Message warning>{inActiveReason}</Message>
  }
  if (debug || error) {
    let x = error || data || {loading}
    x = {...x, ...params, active}
    content = <JSONTree hideRoot={true} data={x} />
  }
  if (content === null) {
    if (strippedPrompt.toLowerCase().includes('in html')) {
      content = <span dangerouslySetInnerHTML={{__html: textOutput}} />
    } else if (strippedPrompt.toLowerCase().includes('in xml')) {
      content = <pre>{strip(textOutput)}</pre>
    } else {
      content = textOutput.split('\n').map((para, i) => <p key={i}>{para}</p>)
    }
  }

  let headerEl = null

  if (header == 'prompt') {
    headerEl = <h2>{debouncedText}</h2>
  }
  return (
    <div ref={ref}>
      {!!content && (
        <SeedButtons
          floated="right"
          retrySeed={retrySeed}
          setRetrySeed={setRetrySeed}
          defaultSeed={seed}
          disabled={loading}
          style={{marginTop: '0px', marginLeft: '0px', transform: 'scale(0.7)'}}
        >
          <TocExperiment prompt={strippedPrompt} text={textOutput} />
        </SeedButtons>
      )}
      {interactive ? (
        <div style={{fontStyle: 'italic'}}>
          <EditableLabel
            text={interactiveText || (text || header || 'Fragen?')}
            onSave={setTnteractiveText}
            selectText
          />
        </div>
      ) : (
        headerEl
      )}

      <div>{content}</div>
      {loader}
    </div>
  )
}

export const AiImage = ({text, width = 256, height = 256, seed = 0}) => {
  let [ref, inView] = useInView({triggerOnce: true, rootMargin: '100%'})
  let apiurl = useSelector(Sel.apiurl)
  let [retrySeed, setRetrySeed] = useResettableState(null, [seed])
  if (retrySeed) {
    seed = retrySeed
  }

  let [debouncedText] = useDebounce(text, 2000, {leading: true})
  let strippedPrompt = strip(debouncedText || '')

  let condition = strippedPrompt.split(' ').length > 1 && inView

  let params = {prompt: strippedPrompt, seed}
  let src = apiurl.href('/api/openai/imagegen', params)
  return (
    <div ref={ref} style={{float: 'right', marginTop: '20px', padding: '10px'}}>
      <SeedButtons
        floated="right"
        retrySeed={retrySeed}
        setRetrySeed={setRetrySeed}
        defaultSeed={seed}
        style={{marginTop: '-5px', marginLeft: '-60px', transform: 'scale(0.5)'}}
      />
      <img
        src={src}
        style={{width, height, float: 'right'}}
        title={strippedPrompt}
        alt={strippedPrompt}
      />
    </div>
  )
}
